import React, { useState } from "react";
import { Send, ChevronDown, MessageCircle, Mail, Phone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

const FAQS = [
  { q: "How do I get WhatsApp deadline alerts?", a: "Go to Profile → enable WhatsApp Alerts and enter your number. You'll receive alerts 30, 14, and 7 days before any tender deadline you're tracking." },
  { q: "Why am I not seeing new tenders?", a: "Scrapers run every 6 hours automatically. Visit Data Sources in the sidebar to check the last scrape time and status of each source." },
  { q: "How does AI Matched work?", a: "Update your company profile with your industry, preferred categories, and states. The AI Matched page will then rank all tenders by fit score for your company." },
  { q: "How do I upgrade or change my plan?", a: "Go to Billing & Plans in the sidebar. You can pay by card (instant activation) or bank transfer (activated within 2 hours after confirmation)." },
  { q: "What happens when my trial ends?", a: "After 14 days a payment screen will appear. All your saved data and settings are preserved — subscribe any time to continue without losing anything." },
  { q: "Can I add more users to my account?", a: "Basic supports 1 user, Professional supports 5, and Enterprise supports unlimited users. Upgrade in Billing & Plans to increase your user limit." },
  { q: "How do I track a tender deadline?", a: "Open any tender and click 'Save Tender'. It will appear in your Saved page and Deadline Pulse dashboard, and you'll receive automatic alerts." },
  { q: "What is the Compliance Hub?", a: "The Compliance Hub tracks your CAC, ITF, PENCOM, NSITF and other mandatory certificates. It alerts you before they expire so you're always bid-ready." },
];

export default function Support() {
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Please fill in subject and message"); return; }
    setSending(true);
    try {
      const token = localStorage.getItem("access_token");
      await fetch(`${API}/payments/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subject, message, user_email: user?.email, user_name: user?.full_name }),
      });
    } catch {}
    setSending(false);
    setSent(true);
    toast.success("Message sent! We'll reply within 24 hours.");
  };

  return (
    <div style={{ maxWidth: "860px" }}>

      {/* Header */}
      <div style={{ marginBottom: "32px" }}>
        <h1 style={{ fontSize: "26px", marginBottom: "6px" }}>Support Center</h1>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Get help with your ProTrackNG account</p>
      </div>

      {/* Contact cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "32px" }}>
        {[
          { icon: Mail, label: "Email Support", value: "support@protrackng.com.ng", sub: "Reply within 24 hours", color: "var(--accent-green)" },
          { icon: MessageCircle, label: "WhatsApp", value: "+234 800 PRO TRACK", sub: "Mon–Fri, 9am–6pm WAT", color: "var(--accent-blue)" },
          { icon: Phone, label: "Response Time", value: "< 24 hours", sub: "For all enquiries", color: "var(--accent-orange)" },
        ].map(({ icon: Icon, label, value, sub, color }) => (
          <div key={label} className="card" style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: `${color}15`, border: `1px solid ${color}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon size={18} color={color} />
            </div>
            <div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: "700", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: "4px" }}>{label}</div>
              <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "2px" }}>{value}</div>
              <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>

        {/* FAQ */}
        <div className="card">
          <h3 style={{ fontSize: "16px", fontWeight: "700", marginBottom: "4px" }}>Frequently Asked Questions</h3>
          <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "20px" }}>Quick answers to common questions</p>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {FAQS.map((f, i) => (
              <div key={i} style={{ borderTop: "1px solid var(--border)" }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", background: "none", border: "none", color: "var(--text-primary)", cursor: "pointer", textAlign: "left", gap: "12px", fontSize: "13px", fontWeight: "600" }}>
                  <span>{f.q}</span>
                  <ChevronDown size={14} color="var(--accent-green)" style={{ flexShrink: 0, transition: "transform 0.2s", transform: openFaq === i ? "rotate(180deg)" : "none" }} />
                </button>
                {openFaq === i && (
                  <p style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.7", paddingBottom: "14px", margin: 0 }}>{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Message form */}
        <div className="card">
          <h3 style={{ fontSize: "16px", fontWeight: "700", marginBottom: "4px" }}>Send a Message</h3>
          <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "20px" }}>
            We'll reply to <strong style={{ color: "var(--accent-green)" }}>{user?.email}</strong>
          </p>

          {sent ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
              <div style={{ fontSize: "16px", fontWeight: "700", marginBottom: "8px" }}>Message received!</div>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "24px", lineHeight: "1.6" }}>
                We'll reply to {user?.email} within 24 hours.
              </div>
              <button onClick={() => { setSent(false); setSubject(""); setMessage(""); }} style={{ padding: "10px 24px", background: "var(--accent-green-dim)", border: "1px solid rgba(0,212,161,0.2)", borderRadius: "10px", color: "var(--accent-green)", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
                Send another
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-secondary)", display: "block", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Your Name</label>
                <input value={user?.full_name || ""} readOnly style={{ width: "100%", opacity: 0.6, cursor: "not-allowed" }} />
              </div>
              <div>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-secondary)", display: "block", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Subject</label>
                <input
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="e.g. WhatsApp alerts not working"
                />
              </div>
              <div>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-secondary)", display: "block", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Message</label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Describe your issue in detail..."
                  rows={5}
                  style={{ width: "100%", padding: "10px 14px", background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: "10px", color: "var(--text-primary)", fontSize: "14px", fontFamily: "inherit", resize: "vertical", outline: "none", lineHeight: "1.6" }}
                />
              </div>
              <button
                onClick={handleSend}
                disabled={sending || !subject.trim() || !message.trim()}
                className="btn btn-primary"
                style={{ justifyContent: "center", gap: "8px", padding: "12px", opacity: (!subject.trim() || !message.trim()) ? 0.5 : 1 }}
              >
                <Send size={15} />
                {sending ? "Sending..." : "Send Message"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
